import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Badge,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { FaSolarPanel, FaDollarSign, FaCalendarAlt, FaChartLine } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

const SolarSavingsCalculator: React.FC = () => {
  const [inputs, setInputs] = useState({
    propertyType: "residential",
    roofSpace: 50,
    dailyUsage: 20,
    electricityRate: 0.30,
    location: "sydney",
  });

  const [results, setResults] = useState<any>(null);

  const handleInputChange = (field: string, value: any) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const calculate = () => {
    const { roofSpace, dailyUsage, electricityRate } = inputs;

    // System size calculation (1 panel = ~2m², 400W per panel)
    const maxPanels = Math.floor(roofSpace / 2);
    const systemSizeKW = (maxPanels * 400) / 1000;
    const recommendedSystemKW = Math.min(systemSizeKW, dailyUsage * 1.3);

    // Annual generation (kWh) - average 4 hours peak sun
    const annualGeneration = recommendedSystemKW * 4 * 365;

    // Annual savings
    const annualSavings = annualGeneration * electricityRate;

    // Payback period (system cost ~$1000/kW installed)
    const systemCost = recommendedSystemKW * 1000;
    const paybackYears = systemCost / annualSavings;

    // 25-year savings
    const totalSavings25Years = annualSavings * 25 - systemCost;

    setResults({
      systemSize: recommendedSystemKW.toFixed(1),
      annualSavings: annualSavings.toFixed(0),
      paybackPeriod: paybackYears.toFixed(1),
      savings25Years: totalSavings25Years.toFixed(0),
      systemCost: systemCost.toFixed(0),
      annualGeneration: annualGeneration.toFixed(0),
    });
  };

  const reset = () => {
    setInputs({
      propertyType: "residential",
      roofSpace: 50,
      dailyUsage: 20,
      electricityRate: 0.30,
      location: "sydney",
    });
    setResults(null);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/solar-savings"
        fallbackTitle="Solar Savings Calculator"
        fallbackDescription="Calculate your potential solar energy savings and system recommendations"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-r, orange.400, yellow.400)"
        color="white"
        py={16}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={4} textAlign="center">
            <Icon as={FaSolarPanel} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Solar Savings Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              Calculate your solar system size, annual savings, and return on investment
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Two-Column Layout: Inputs Left | Results Right */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          {/* LEFT COLUMN - Inputs */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="orange.600">
                  Property Details
                </Heading>

                <FormControl>
                  <FormLabel>Property Type</FormLabel>
                  <Select
                    value={inputs.propertyType}
                    onChange={(e) => handleInputChange("propertyType", e.target.value)}
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Available Roof Space (m²)</FormLabel>
                  <Input
                    type="number"
                    value={inputs.roofSpace}
                    onChange={(e) => handleInputChange("roofSpace", parseFloat(e.target.value) || 0)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Typical residential roof: 40-100m²
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Daily Electricity Usage (kWh)</FormLabel>
                  <Input
                    type="number"
                    value={inputs.dailyUsage}
                    onChange={(e) => handleInputChange("dailyUsage", parseFloat(e.target.value) || 0)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Check your electricity bill for average daily usage
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Electricity Rate ($/kWh)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={inputs.electricityRate}
                    onChange={(e) =>
                      handleInputChange("electricityRate", parseFloat(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Average Australian rate: $0.25-$0.35/kWh
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Select
                    value={inputs.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                  >
                    <option value="sydney">Sydney, NSW</option>
                    <option value="melbourne">Melbourne, VIC</option>
                    <option value="brisbane">Brisbane, QLD</option>
                    <option value="perth">Perth, WA</option>
                    <option value="adelaide">Adelaide, SA</option>
                    <option value="hobart">Hobart, TAS</option>
                    <option value="darwin">Darwin, NT</option>
                    <option value="canberra">Canberra, ACT</option>
                  </Select>
                </FormControl>

                <HStack spacing={4}>
                  <Button colorScheme="orange" size="lg" onClick={calculate} flex={1}>
                    Calculate Savings
                  </Button>
                  <Button variant="outline" size="lg" onClick={reset}>
                    Reset
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* RIGHT COLUMN - Results */}
          <VStack spacing={6} align="stretch">
            {results ? (
              <>
                <Card bg="orange.50" borderWidth={2} borderColor="orange.300">
                  <CardBody>
                    <VStack spacing={4}>
                      <Badge colorScheme="orange" fontSize="md" px={4} py={2}>
                        Recommended System
                      </Badge>
                      <HStack>
                        <Icon as={FaSolarPanel} boxSize={8} color="orange.500" />
                        <Stat textAlign="center">
                          <StatNumber fontSize="4xl" color="orange.600">
                            {results.systemSize} kW
                          </StatNumber>
                          <StatLabel>Solar System Size</StatLabel>
                        </Stat>
                      </HStack>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        Estimated system cost: ${parseInt(results.systemCost).toLocaleString()}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>

                <SimpleGrid columns={2} spacing={4}>
                  <Card>
                    <CardBody>
                      <Stat>
                        <HStack mb={2}>
                          <Icon as={FaDollarSign} color="green.500" />
                          <StatLabel>Annual Savings</StatLabel>
                        </HStack>
                        <StatNumber color="green.600">
                          ${parseInt(results.annualSavings).toLocaleString()}
                        </StatNumber>
                        <StatHelpText>Per year</StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <Stat>
                        <HStack mb={2}>
                          <Icon as={FaCalendarAlt} color="blue.500" />
                          <StatLabel>Payback Period</StatLabel>
                        </HStack>
                        <StatNumber color="blue.600">{results.paybackPeriod} years</StatNumber>
                        <StatHelpText>Break-even time</StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                <Card bg="green.50" borderWidth={2} borderColor="green.300">
                  <CardBody>
                    <Stat textAlign="center">
                      <HStack justify="center" mb={2}>
                        <Icon as={FaChartLine} color="green.500" boxSize={6} />
                        <StatLabel fontSize="lg">25-Year Total Savings</StatLabel>
                      </HStack>
                      <StatNumber fontSize="4xl" color="green.600">
                        ${parseInt(results.savings25Years).toLocaleString()}
                      </StatNumber>
                      <StatHelpText>After system cost</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <VStack spacing={3} align="stretch">
                      <Text fontWeight="bold" color="gray.700">
                        System Performance
                      </Text>
                      <Divider />
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Annual Generation:
                        </Text>
                        <Text fontWeight="semibold">
                          {parseInt(results.annualGeneration).toLocaleString()} kWh
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Daily Generation:
                        </Text>
                        <Text fontWeight="semibold">
                          {(parseFloat(results.annualGeneration) / 365).toFixed(1)} kWh
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Coverage:
                        </Text>
                        <Text fontWeight="semibold">
                          {(
                            ((parseFloat(results.annualGeneration) / 365) / inputs.dailyUsage) *
                            100
                          ).toFixed(0)}
                          % of daily usage
                        </Text>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>

                <Box bg="blue.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
                  <Text fontSize="sm" color="gray.700">
                    <strong>Note:</strong> Calculations are estimates based on average conditions. Actual
                    savings may vary based on system orientation, shading, weather patterns, and energy
                    usage habits. Consult with a solar installer for precise quotes.
                  </Text>
                </Box>

                <LeadCaptureForm
                  calculatorType={CalculatorType.SolarSavings}
                  calculatorName="Solar Savings Calculator"
                  calculatorPath="/calculators/solar-savings"
                  inputData={inputs}
                  resultData={results}
                  hasResults={!!results}
                  accentColor="orange"
                />
              </>
            ) : (
              <Card h="full">
                <CardBody>
                  <VStack spacing={4} justify="center" h="full" color="gray.400">
                    <Icon as={FaSolarPanel} boxSize={16} />
                    <Text fontSize="lg" textAlign="center">
                      Enter your property details to see solar savings estimate
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </SimpleGrid>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolarSavingsCalculator;
